import { useCallback, useEffect } from "react";
import type { InsightsMethodMap } from "search-insights";
import useUserStore from "../../store/userStore";

type InteractionType =
  | "view"
  | "quickplay"
  | "play"
  | "reaction-like"
  | "reaction-dislike"
  | "add-comment";

interface TrackInteractionOptions {
  interactionType: InteractionType;
  objectId: string;
  queryId: string;
  itemType: "post" | "user";
}

interface TrackerHookResult {
  trackInteraction: (
    options: TrackInteractionOptions
  ) => Promise<void | string | number | boolean>;
}

type InsightsClientType = typeof import("search-insights")["default"];

let insightClientRef: InsightsClientType | null = null;
let hasInitialized = false;

async function loadInsightsClient(): Promise<InsightsClientType> {
  if (insightClientRef) {
    return insightClientRef;
  }

  const module = await import("search-insights");
  insightClientRef = module.default;
  return module.default;
}

export function useTracker(): TrackerHookResult {
  const stateUserId = useUserStore((state) => state.user?.id);

  useEffect(() => {
    let isMounted = true;

    async function initInsights() {
      const client = await loadInsightsClient();
      if (!isMounted) {
        return;
      }

      if (!hasInitialized) {
        client("init", {
          appId: (import.meta as any).env.VITE_ALGOLIA_APP_ID as string,
          apiKey: (import.meta as any).env.VITE_ALGOLIA_API_KEY as string,
        });
        hasInitialized = true;
      }

      if (stateUserId) {
        client("setAuthenticatedUserToken", stateUserId);
      }
    }

    initInsights();

    return () => {
      isMounted = false;
    };
  }, [stateUserId]);

  /**
   * Maps interactionType and queryId presence to Algolia Insights event method.
   *
   * Mapping:
   * - "viewedObjectIDs": For view events (post-view, track-quickplay). queryId (optional, include if available)
   * - "clickedObjectIDsAfterSearch": For pre-search click events that are enhanced with a queryId (clicked + queryId)
   * - "clickedObjectIDs": For click events (reaction-like/dislike, track-play, add-comment) not associated with a search/queryId
   * - "convertedObjectIDsAfterSearch": For conversion events with a queryId (reaction-like, track-play, add-comment)
   * - "convertedObjectIDs": For conversion events without a queryId (reaction-like, track-play, add-comment)
   */
  function mapInteractionToFunctionName(
    interactionType: InteractionType,
    queryId: string
  ): string {
    switch (interactionType) {
      case "view":
      case "quickplay":
        return "viewedObjectIDs"; // Algolia recommends views go here (queryId optional)
      case "reaction-like":
      case "play":
      case "add-comment":
        // These are conversions in Algolia's model
        return queryId ? "convertedObjectIDsAfterSearch" : "convertedObjectIDs";
      case "reaction-dislike":
        // Could be modeled as a click event, NOT a conversion
        return queryId ? "clickedObjectIDsAfterSearch" : "clickedObjectIDs";
      default:
        // For any other action fallback to click event
        return queryId ? "clickedObjectIDsAfterSearch" : "clickedObjectIDs";
    }
  }

  const trackInteraction = useCallback(
    async ({
      interactionType,
      objectId,
      queryId,
      itemType = "post",
    }: TrackInteractionOptions): Promise<void | number | string | boolean> => {
      const event = mapInteractionToFunctionName(
        interactionType,
        queryId
      ) as keyof InsightsMethodMap;

      const isDev = !!(import.meta as any).env.DEV;
      const INDEX_PREFIX = isDev ? "dev_" : "";

      const client = await loadInsightsClient();
      const eventLogResult = client(event, {
        eventName: interactionType,
        index: `${INDEX_PREFIX}collection_${itemType}s`,
        objectIDs: [objectId],
        queryId,
        userToken: stateUserId,
      });

      return eventLogResult;
    },
    [stateUserId]
  );

  return { trackInteraction };
}
